import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  IPerfil,
  ISelectOption,
  TipoPerfil,
  TIPO_PERFIL_OPTIONS,
  SITUACAO_OPTIONS,
  STATUS_OPTIONS,
} from "../types/perfil.types";
import { InputDecimalPtBr } from "../../components/InputDecimalPtBr";

// Estilos customizados específicos para esta página - SEM BORDA
const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f0f0f0",
    borderRadius: 0,
    minHeight: 56,
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#3C90AD",
    fontWeight: 500,
    fontSize: "0.8rem",
    "&.Mui-focused": {
      color: "#3C90AD",
    },
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
};

const selectSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "20px 14px 8px 14px",
  },
};

const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
};

interface TabFichaProps {
  perfil: Partial<IPerfil>;
  onChange: (campo: keyof IPerfil, valor: any) => void;
  referencias: ISelectOption[];
  linhas: ISelectOption[];
  ligas: ISelectOption[];
  temperas: ISelectOption[];
  prenativos: ISelectOption[];
  classeIds: ISelectOption[];
  unidadesValor: ISelectOption[];
  onOpenMidias: () => void;
  // FASE 05 — S3+/PO 2026-05-13: feedback em tempo real para codigo duplicado.
  codigoDuplicado?: boolean;
  validandoCodigo?: boolean;
}

export const TabFicha: React.FC<TabFichaProps> = ({
  perfil,
  onChange,
  referencias,
  linhas,
  ligas,
  temperas,
  prenativos,
  classeIds,
  unidadesValor,
  onOpenMidias,
  codigoDuplicado = false,
  validandoCodigo = false,
}) => {
  return (
    <Box>
      {/* FASE 05 — 6ª validação/PO 2026-08-11: Situação numa linha ACIMA do container principal,
          para que Código | Código interno | Status fiquem alinhados ao topo do quadro da imagem
          (mesmo padrão de Componente/Kit/Superfície). Situação é somente-leitura — as transições
          acontecem só pelos botões Homologar/Concluir/Disponibilizar. */}
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel sx={labelSx}>Situação</InputLabel>
            <Select
              value={perfil.idSituacao || 1}
              label="Situação"
              disabled
              sx={selectSx}
            >
              {SITUACAO_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Container principal com Imagem à esquerda e campos à direita */}
      <Grid container spacing={3}>
        {/* Coluna Esquerda - Imagem do Produto */}
        <Grid item xs={12} md={3}>
          {/* Container da Imagem - COM BORDA CINZA */}
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              p: 2,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#333", mb: 2, fontWeight: 500 }}
            >
              Imagem do produto
            </Typography>
            
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 180,
                backgroundColor: "#fff",
              }}
            >
              {perfil.figuraUrl ? (
                <img
                  src={perfil.figuraUrl}
                  alt="Imagem do produto"
                  style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma imagem
                </Typography>
              )}
            </Box>

            {perfil.figuraNomeArquivo && (
              <Typography
                variant="caption"
                sx={{ color: "#999", textAlign: "center", display: "block", mt: 2 }}
              >
                {perfil.figuraNomeArquivo}
              </Typography>
            )}
          </Box>

          {/* Botão FORA do container da imagem */}
          <Button
            variant="contained"
            fullWidth
            onClick={onOpenMidias}
            sx={{
              backgroundColor: "#3C90AD",
              "&:hover": { backgroundColor: "#2d6f87" },
              textTransform: "none",
              py: 1.2,
            }}
          >
            GERENCIAMENTO DE MÍDIAS
          </Button>
        </Grid>

        {/* Coluna Direita - Campos do Formulário */}
        <Grid item xs={12} md={9}>
          {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: mockup do PO (image6) —
              linha Código | Código interno e Descrição em linha própria (campo largo). */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código"
                value={perfil.codigo || ""}
                onChange={(e) => onChange("codigo", e.target.value.toUpperCase())}
                required
                inputProps={{ maxLength: 20 }}
                error={codigoDuplicado}
                // FASE 05 — 4ª validação/Frente 9 2026-07-30: mensagem padronizada do blur
                // (a mensagem longa fica só no Salvar).
                helperText={codigoDuplicado ? "Código já existe" : validandoCodigo ? "Verificando..." : ""}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Código interno"
                value={perfil.codigoInterno || ""}
                onChange={(e) => onChange("codigoInterno", e.target.value)}
                inputProps={{ maxLength: 50 }}
                sx={inputSx}
              />
            </Grid>
            {/* FASE 05 — 6ª validação/PO 2026-08-11: Status na 3ª coluna, alinhado com
                Código/Código interno (Situação fica na linha acima). */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx}>Status</InputLabel>
                <Select
                  value={perfil.idStatus || 1}
                  label="Status"
                  onChange={(e) => onChange("idStatus", e.target.value)}
                  sx={selectSx}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Linha: Descrição */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={perfil.descricao || ""}
                onChange={(e) => onChange("descricao", e.target.value)}
                required
                inputProps={{ maxLength: 200 }}
                sx={inputSx}
              />
            </Grid>
          </Grid>

          {/* Linha: Referência, Linha, Peso em KG por mt */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Referência</InputLabel>
                <Select
                  value={perfil.idReferencia || ""}
                  label="Referência"
                  onChange={(e) => onChange("idReferencia", e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {referencias.map((ref) => (
                    <MenuItem key={ref.id} value={ref.id}>
                      {ref.nome || ref.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Linha</InputLabel>
                <Select
                  value={perfil.idLinha || ""}
                  label="Linha"
                  onChange={(e) => onChange("idLinha", e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {linhas.map((linha) => (
                    <MenuItem key={linha.id} value={linha.id}>
                      {linha.nome || linha.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {/* FASE 05 — 2ª validação/PO 2026-07-24 (F5): decimal pt-BR aceita "0,123". */}
              <InputDecimalPtBr
                fullWidth
                required
                label="Peso em KG por mt"
                value={perfil.pesoKgPorMetro}
                onChange={(v) => onChange("pesoKgPorMetro", v ?? 0)}
                inputProps={{ maxLength: 12 }}
                sx={inputSx}
              />
            </Grid>
          </Grid>

          {/* Linha: Prenativo, ClasseId, Unidade de valorização */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Prenativo</InputLabel>
                <Select
                  value={perfil.idPrenativo || ""}
                  label="Prenativo"
                  onChange={(e) => onChange("idPrenativo", e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {prenativos.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.nome || p.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>ClasseId</InputLabel>
                <Select
                  value={perfil.idClasseId || ""}
                  label="ClasseId"
                  onChange={(e) => onChange("idClasseId", e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {classeIds.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nome || c.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {/* FASE 05 — 2ª validação/PO 2026-07-24 (F7): apresenta o cadastro completo
                  de Unidades (removido o filtro hardcoded metro/kg/barra). */}
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Unidade de valorização</InputLabel>
                <Select
                  value={perfil.idUnidadeValor || ""}
                  label="Unidade de valorização"
                  onChange={(e) => onChange("idUnidadeValor", e.target.value || undefined)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {unidadesValor.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.nome || u.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Linha: Tipo do perfil, Considerar Perda, Liga, Tempera */}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={labelSx}>Tipo do perfil</InputLabel>
                <Select
                  // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 6): default "Cor Única";
                  // a opção "Não" saiu da lista (reversão da P1 de 24/07).
                  value={perfil.tipoPerfil || "cor_unica"}
                  label="Tipo do perfil"
                  onChange={(e) => onChange("tipoPerfil", e.target.value as TipoPerfil)}
                  sx={selectSx}
                >
                  {TIPO_PERFIL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                  {/* Cadastro antigo gravado com "Não" abre sem erro, mas a opção não é oferecida */}
                  {perfil.tipoPerfil === "nao" && (
                    <MenuItem key="nao" value="nao" disabled>
                      Não (valor antigo)
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {/* FASE 05 — 2ª validação/PO 2026-07-24 (F10): Considerar Perda (Sim default/Não),
                  entre Tipo do Perfil e Liga. Grava no legado invertido DesconsiderarPerdaBarra. */}
              <FormControl fullWidth>
                <InputLabel sx={labelSx}>Considerar Perda</InputLabel>
                <Select
                  value={perfil.considerarPerda === false ? "nao" : "sim"}
                  label="Considerar Perda"
                  onChange={(e) => onChange("considerarPerda", e.target.value === "sim")}
                  sx={selectSx}
                >
                  <MenuItem value="sim">Sim</MenuItem>
                  <MenuItem value="nao">Não</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Liga</InputLabel>
                <Select
                  value={perfil.idLiga || ""}
                  label="Liga"
                  onChange={(e) => onChange("idLiga", e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {ligas.map((liga) => (
                    <MenuItem key={liga.id} value={liga.id}>
                      {liga.nome || liga.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth required>
                <InputLabel sx={labelSx} required>Tempera</InputLabel>
                <Select
                  value={perfil.idTempera || ""}
                  label="Tempera"
                  onChange={(e) => onChange("idTempera", e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em>Selecione...</em>
                  </MenuItem>
                  {temperas.map((tempera) => (
                    <MenuItem key={tempera.id} value={tempera.id}>
                      {tempera.nome || tempera.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
